/**
 * Insight (INSIGHT-1) — the handful of reports a trade actually opens.
 *
 * Everything here is one request. The tiles at the top are summed from the
 * tables below them, and fetched separately a write landing between two calls
 * would show a headline that does not add up to its own detail — a report
 * that visibly contradicts itself is worse than no report.
 *
 * The order is the order an owner reads it in: what came in, what is at risk
 * right now, what is owed, and only then the slower questions about where the
 * hours went and whether quoting is working. Revenue history is last of the
 * money sections for the same reason — it is the one you look at monthly, not
 * the one you open the screen for.
 *
 * Nothing invents a figure it does not have. A month with no revenue is a
 * zero; a job with no revenue yet has no margin at all, and shows "—" rather
 * than 0%, because a dashboard reading 0% has somebody repricing work that is
 * perfectly fine (D43).
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../lib/api";
import { OFFICE, useAuth } from "../lib/auth";
import { day, hours as showHours, marginTone, money, moneyShort, percent, titleCase } from "../lib/format";
import {
  Bar,
  Card,
  Chip,
  DataTable,
  Empty,
  ErrorState,
  Loading,
  NotBuiltYet,
  PageHeader,
  Tile,
  Tiles,
} from "../components/ui";
import type { ConversionOut, DebtorOut, MonthOut, OverviewOut, ReportJobOut, TechOut, TypeOut } from "../lib/types";

const WINDOWS = [30, 90, 180, 365] as const;

export default function Insight() {
  const { can } = useAuth();
  const [days, setDays] = useState<number>(90);

  const report = useQuery({
    queryKey: ["insight", days],
    queryFn: () => api.reports.overview({ days, months: 12 }),
    placeholderData: (previous) => previous,
  });

  // The server gates these too; this is so the nav item does not lead to a
  // wall of 403s for a tech who found it.
  if (!can(...OFFICE)) {
    return (
      <div className="page">
        <NotBuiltYet
          module="Insight"
          card="INSIGHT-1"
          what="The reports are the business's money — revenue, debtors, margin by work type — so they're kept to the office. Your own job's margin is on the job card, live, while you're still on site."
        />
      </div>
    );
  }

  if (report.isPending) return <Loading what="Working out the numbers" />;
  if (report.error) return <ErrorState error={report.error} onRetry={() => void report.refetch()} />;

  const data: OverviewOut = report.data;
  const top = data.headline;
  const thisMonth = data.months[data.months.length - 1];

  return (
    <div className="page">
      <PageHeader title="Insight">
        {`Everything on this page is worked out from the jobs themselves, not from a stored total — ${day(
          data.since,
        )} to ${day(data.until)} where a window applies.`}
      </PageHeader>

      <Tiles>
        <Tile
          label="Billed this month"
          value={moneyShort(top.month_net_cents)}
          detail={
            thisMonth && thisMonth.credited_cents
              ? `net of ${moneyShort(thisMonth.credited_cents)} credited`
              : "ex GST, sent invoices"
          }
        />
        <Tile
          label="Collected this month"
          value={moneyShort(top.month_paid_cents)}
          detail="money that actually arrived"
          tone={top.month_paid_cents > 0 ? "ok" : undefined}
        />
        <Tile
          label="Done, not billed"
          value={moneyShort(top.wip_cents)}
          detail={top.wip_oldest_days ? `oldest ${top.wip_oldest_days} days` : "nothing waiting"}
          tone={top.wip_oldest_days > 30 ? "warn" : undefined}
        />
        <Tile
          label="Owed"
          value={moneyShort(top.owed_cents)}
          detail={top.owed_overdue_cents ? `${moneyShort(top.owed_overdue_cents)} of it overdue` : "none overdue"}
          tone={top.owed_overdue_cents > 0 ? "warn" : undefined}
        />
        <Tile
          label="Margin on finished work"
          value={top.margin_bp === null ? "—" : percent(top.margin_bp)}
          detail={
            top.margin_bp === null ? "nothing finished in the window" : `across ${data.types.length} kinds of work`
          }
          tone={top.margin_bp === null ? undefined : marginTone(top.margin_bp)}
        />
        <Tile
          label="Losing money now"
          value={top.losing_count}
          detail={top.losing_count ? "live jobs below break-even" : "nothing underwater"}
          tone={top.losing_count ? "crit" : "ok"}
        />
      </Tiles>

      <div className="toolbar">
        <span className="tiny muted">Margin and utilisation over the last</span>
        <div className="segmented" role="group" aria-label="Window">
          {WINDOWS.map((w) => (
            <button key={w} type="button" className={days === w ? "active" : ""} onClick={() => setDays(w)}>
              {w === 365 ? "year" : `${w} days`}
            </button>
          ))}
        </div>
        {report.isFetching && <span className="tiny muted">updating…</span>}
      </div>

      <LosingMoney rows={data.losing} />
      <WorkInProgress rows={data.wip} />
      <MarginByType rows={data.types} />
      <Debtors rows={data.debtors} />
      <Revenue rows={data.months} />
      <Utilisation rows={data.techs} />
      <Conversion rows={data.conversion} />
    </div>
  );
}

/* --------------------------------------------------------------- the money */

function LosingMoney({ rows }: { rows: ReportJobOut[] }) {
  return (
    <Card
      title="Losing money right now"
      subtitle={rows.length ? `${rows.length} live, worst first` : undefined}
      padded={rows.length === 0}
    >
      {rows.length === 0 ? (
        <Empty title="Nothing underwater">
          Every live job is above break-even. This is the list to check first when one isn't — while it can still be
          fixed, rather than at month end.
        </Empty>
      ) : (
        <DataTable
          columns={[
            { key: "job", label: "Job" },
            { key: "customer", label: "Customer", hideOnPhone: true },
            { key: "tech", label: "With", hideOnPhone: true },
            { key: "hours", label: "Hours", align: "right", hideOnPhone: true },
            { key: "rev", label: "Earning", align: "right" },
            { key: "margin", label: "Margin", align: "right" },
          ]}
        >
          <>
            {rows.map((job) => (
              <tr key={job.id}>
                <td>
                  <Link to={`/jobs/${job.id}`}>
                    <strong className="mono">{job.number}</strong>
                  </Link>
                  <div className="tiny muted">{job.title}</div>
                </td>
                <td className="phone-hide">{job.customer}</td>
                <td className="phone-hide tiny">{job.assigned_to ?? "unassigned"}</td>
                <td className="right phone-hide tiny">
                  {showHours(job.hours_actual)}
                  {job.hours_quoted ? <span className="muted"> / {showHours(job.hours_quoted)}</span> : null}
                </td>
                <td className="right">{money(job.revenue_cents)}</td>
                <td className="right">
                  <MarginCell bp={job.margin_bp} />
                </td>
              </tr>
            ))}
          </>
        </DataTable>
      )}
    </Card>
  );
}

function WorkInProgress({ rows }: { rows: ReportJobOut[] }) {
  const total = rows.reduce((sum, r) => sum + r.revenue_cents, 0);
  return (
    <Card
      title="Done, not billed"
      subtitle={rows.length ? `${money(total)} across ${rows.length} job${rows.length === 1 ? "" : "s"}` : undefined}
      padded={rows.length === 0}
    >
      {rows.length === 0 ? (
        <Empty title="Everything worked on has been billed">
          Money already spent and not yet invoiced shows up here, aged from the first hour booked on the job.
        </Empty>
      ) : (
        <DataTable
          columns={[
            { key: "job", label: "Job" },
            { key: "customer", label: "Customer", hideOnPhone: true },
            { key: "status", label: "Status", hideOnPhone: true },
            { key: "age", label: "Waiting", align: "right" },
            { key: "value", label: "Unbilled", align: "right" },
          ]}
        >
          <>
            {rows.map((job) => (
              <tr key={job.id}>
                <td>
                  <Link to={`/jobs/${job.id}`}>
                    <strong className="mono">{job.number}</strong>
                  </Link>
                  <div className="tiny muted">{job.title}</div>
                </td>
                <td className="phone-hide">{job.customer}</td>
                <td className="phone-hide">
                  <Chip>{titleCase(job.status)}</Chip>
                </td>
                <td className="right">
                  {job.age_days === null ? (
                    <span className="muted tiny">no hours booked</span>
                  ) : (
                    <Chip tone={job.age_days > 30 ? "crit" : job.age_days > 14 ? "warn" : "neutral"}>
                      {job.age_days} days
                    </Chip>
                  )}
                </td>
                <td className="right">{money(job.revenue_cents)}</td>
              </tr>
            ))}
          </>
        </DataTable>
      )}
    </Card>
  );
}

function MarginByType({ rows }: { rows: TypeOut[] }) {
  const best = Math.max(1, ...rows.map((r) => r.revenue_cents));
  return (
    <Card
      title="What each kind of work earns"
      subtitle="The report that changes what gets quoted"
      padded={rows.length === 0}
    >
      {rows.length === 0 ? (
        <Empty title="Nothing finished in this window">
          A job counts here once it's completed. Jobs with no completion date are left out rather than guessed at.
        </Empty>
      ) : (
        <DataTable
          columns={[
            { key: "type", label: "Work" },
            { key: "jobs", label: "Jobs", align: "right" },
            { key: "hours", label: "Hours", align: "right", hideOnPhone: true },
            { key: "rev", label: "Earned", align: "right" },
            { key: "cost", label: "Cost", align: "right", hideOnPhone: true },
            { key: "margin", label: "Margin", align: "right" },
          ]}
        >
          <>
            {rows.map((row) => (
              <tr key={row.job_type}>
                <td>
                  <strong>{titleCase(row.job_type)}</strong>
                  {/* The bar is revenue against the biggest type, so the
                      mix is readable without a second chart. */}
                  <Bar value={(row.revenue_cents / best) * 100} tone="accent" />
                </td>
                <td className="right">{row.jobs}</td>
                <td className="right phone-hide tiny">{showHours(row.hours)}</td>
                <td className="right">{money(row.revenue_cents)}</td>
                <td className="right phone-hide">{money(row.cost_cents)}</td>
                <td className="right">
                  <MarginCell bp={row.margin_bp} />
                </td>
              </tr>
            ))}
          </>
        </DataTable>
      )}
    </Card>
  );
}

function Debtors({ rows }: { rows: DebtorOut[] }) {
  const total = rows.reduce((sum, r) => sum + r.outstanding_cents, 0);
  return (
    <Card
      title="Who owes money"
      subtitle={rows.length ? `${money(total)} outstanding` : undefined}
      padded={rows.length === 0}
    >
      {rows.length === 0 ? (
        <Empty title="Nothing outstanding">Every sent invoice is paid or credited.</Empty>
      ) : (
        <DataTable
          columns={[
            { key: "customer", label: "Customer" },
            { key: "current", label: "Not yet due", align: "right", hideOnPhone: true },
            { key: "30", label: "To 30 days", align: "right", hideOnPhone: true },
            { key: "60", label: "To 60", align: "right", hideOnPhone: true },
            { key: "90", label: "To 90", align: "right", hideOnPhone: true },
            { key: "90+", label: "Over 90", align: "right" },
            { key: "total", label: "Owed", align: "right" },
          ]}
        >
          <>
            {rows.map((row) => (
              <tr key={row.customer_id}>
                <td>
                  <Link to={`/customers/${row.customer_id}`}>{row.customer}</Link>
                  {row.oldest_days > 0 && (
                    <div className="tiny muted">{row.oldest_days} days past due at the worst</div>
                  )}
                </td>
                {["current", "30", "60", "90"].map((bucket) => (
                  <td key={bucket} className="right phone-hide tiny">
                    {row.buckets[bucket] ? money(row.buckets[bucket]!) : <span className="muted">—</span>}
                  </td>
                ))}
                <td className="right">
                  {row.buckets["90+"] ? (
                    <span className="text-crit">{money(row.buckets["90+"]!)}</span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td className="right">
                  <strong>{money(row.outstanding_cents)}</strong>
                </td>
              </tr>
            ))}
          </>
        </DataTable>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------ the history */

function Revenue({ rows }: { rows: MonthOut[] }) {
  const peak = Math.max(1, ...rows.flatMap((r) => [r.net_cents, r.paid_cents]));
  return (
    <Card title="Billed and collected" subtitle="Twelve months, ex GST, net of credits">
      {/* Two charts rather than two colours on one. Both are dollars on the
          same scale, so they compare by eye, and a single series needs no
          legend — which is also how this avoids a second hue, since the only
          two Keystone has that would fit are too close to tell apart. */}
      <MonthChart rows={rows} field="net_cents" peak={peak} label="Invoiced" />
      <MonthChart rows={rows} field="paid_cents" peak={peak} label="Collected" />
      <details className="tiny" style={{ marginTop: 10 }}>
        <summary className="muted">The numbers behind it</summary>
        <DataTable
          columns={[
            { key: "month", label: "Month" },
            { key: "invoiced", label: "Invoiced", align: "right" },
            { key: "credited", label: "Credited", align: "right" },
            { key: "net", label: "Net", align: "right" },
            { key: "paid", label: "Collected", align: "right" },
          ]}
        >
          <>
            {rows.map((row) => (
              <tr key={row.month}>
                <td>{monthLabel(row.month, true)}</td>
                <td className="right">{money(row.invoiced_cents)}</td>
                <td className="right">
                  {row.credited_cents ? money(row.credited_cents) : <span className="muted">—</span>}
                </td>
                <td className="right">
                  <strong>{money(row.net_cents)}</strong>
                </td>
                <td className="right">{money(row.paid_cents)}</td>
              </tr>
            ))}
          </>
        </DataTable>
      </details>
    </Card>
  );
}

/**
 * A column per month, drawn as plain SVG.
 *
 * No charting library: this is one series of twelve bars, and a dependency
 * that renders it would be larger than the rest of the app's vendor bundle.
 * Colour comes from the theme token, so dark mode is handled by the same
 * stylesheet as everything else rather than by a second palette.
 */
function MonthChart({
  rows,
  field,
  peak,
  label,
}: {
  rows: MonthOut[];
  field: "net_cents" | "paid_cents";
  peak: number;
  label: string;
}) {
  const H = 96;
  const GAP = 2; // the surface gap that keeps adjacent columns separate
  const width = 100 / Math.max(rows.length, 1);
  const anything = rows.some((r) => r[field] > 0);
  // One number on the chart rather than twelve: the tallest column is the
  // only one whose value cannot be read off the others.
  const tallest = rows.reduce((best, r, i) => (r[field] > (rows[best]?.[field] ?? 0) ? i : best), 0);

  return (
    <figure className="chart" aria-label={`${label} by month`}>
      <figcaption className="label">{label}</figcaption>
      <div className="chart-plot" style={{ height: H }}>
        {rows.map((row, i) => {
          const value = row[field];
          // Capped below the ceiling so the one labelled column has room for
          // its number above it rather than printed over itself.
          const pct = Math.max(0, (value / peak) * 86);
          return (
            <div
              key={row.month}
              className="chart-col"
              style={{ left: `${i * width}%`, width: `calc(${width}% - ${GAP}px)` }}
              title={`${monthLabel(row.month, true)} — ${money(value)}`}
            >
              {i === tallest && value > 0 && (
                <b className="chart-peak" style={{ bottom: `calc(${pct}% + 3px)` }}>
                  {moneyShort(value)}
                </b>
              )}
              <i
                className="chart-bar"
                style={{ height: `${pct}%` }}
                /* A zero month is a fact, so it keeps its slot and shows a
                   hairline rather than disappearing. */
              />
            </div>
          );
        })}
      </div>
      <div className="chart-axis">
        {rows.map((row, i) => (
          <span key={row.month} style={{ left: `${i * width}%`, width: `${width}%` }}>
            {/* Every third month, so twelve labels do not collide on a phone. */}
            {i % 3 === rows.length % 3 ? monthLabel(row.month) : ""}
          </span>
        ))}
      </div>
      {!anything && <p className="tiny muted">Nothing {label.toLowerCase()} in this period.</p>}
    </figure>
  );
}

function monthLabel(iso: string, withYear = false): string {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-AU", { month: "short", ...(withYear ? { year: "numeric" } : {}) });
}

/* ------------------------------------------------------------- the people */

function Utilisation({ rows }: { rows: TechOut[] }) {
  const counted = rows.filter((r) => r.counts_toward_target);
  const apprentices = rows.filter((r) => !r.counts_toward_target);
  return (
    <Card title="Where the hours went" subtitle="Billable against worked" padded={rows.length === 0}>
      {rows.length === 0 ? (
        <Empty title="No hours booked in this window">
          Utilisation is billable minutes over every minute booked, including admin and training.
        </Empty>
      ) : (
        <DataTable
          columns={[
            { key: "who", label: "Who" },
            { key: "billable", label: "Billable", align: "right", hideOnPhone: true },
            { key: "worked", label: "Worked", align: "right", hideOnPhone: true },
            { key: "overhead", label: "Off the tools", align: "right", hideOnPhone: true },
            { key: "pct", label: "Utilisation", align: "right" },
          ]}
        >
          <>
            {[...counted, ...apprentices].map((row) => (
              <tr key={row.user_id}>
                <td>
                  <strong>{row.name}</strong>
                  {/* Kept separate rather than averaged in: a first-year on
                      60% and two A-grade techs on 85% average to a number
                      that describes nobody. */}
                  {!row.counts_toward_target && <Chip>not counted toward the target</Chip>}
                  <Bar value={(row.utilisation_bp ?? 0) / 100} tone={row.counts_toward_target ? "accent" : "neutral"} />
                </td>
                <td className="right phone-hide tiny">{showHours(row.billable_minutes / 60)}</td>
                <td className="right phone-hide tiny">{showHours(row.total_minutes / 60)}</td>
                <td className="right phone-hide tiny">
                  {row.overhead_minutes ? showHours(row.overhead_minutes / 60) : <span className="muted">—</span>}
                </td>
                <td className="right">
                  <strong>{row.utilisation_bp === null ? "—" : percent(row.utilisation_bp, 0)}</strong>
                </td>
              </tr>
            ))}
          </>
        </DataTable>
      )}
    </Card>
  );
}

function Conversion({ rows }: { rows: ConversionOut[] }) {
  const sent = rows.reduce((n, r) => n + r.sent, 0);
  return (
    <Card
      title="Quoting"
      subtitle={sent ? "An acceptance counts against the month the quote went out" : undefined}
      padded={sent === 0}
    >
      {sent === 0 ? (
        <Empty title="No quotes sent in this period">
          Conversion is counted against the month a quote was sent, so a quote accepted two months later still credits
          the month it went out.
        </Empty>
      ) : (
        <DataTable
          columns={[
            { key: "month", label: "Month" },
            { key: "sent", label: "Sent", align: "right" },
            { key: "sentv", label: "Value out", align: "right", hideOnPhone: true },
            { key: "won", label: "Won", align: "right" },
            { key: "wonv", label: "Value won", align: "right", hideOnPhone: true },
            { key: "rate", label: "Rate", align: "right" },
          ]}
        >
          <>
            {rows.map((row) => (
              <tr key={row.month}>
                <td>{monthLabel(row.month, true)}</td>
                <td className="right">{row.sent || <span className="muted">—</span>}</td>
                <td className="right phone-hide tiny">{row.sent_cents ? money(row.sent_cents) : "—"}</td>
                <td className="right">{row.accepted || <span className="muted">—</span>}</td>
                <td className="right phone-hide tiny">{row.accepted_cents ? money(row.accepted_cents) : "—"}</td>
                <td className="right">
                  <strong>{row.rate_bp === null ? "—" : percent(row.rate_bp, 0)}</strong>
                </td>
              </tr>
            ))}
          </>
        </DataTable>
      )}
    </Card>
  );
}

/** A margin, or an honest dash when there is no revenue to take one of. */
function MarginCell({ bp }: { bp: number | null }) {
  if (bp === null) return <span className="muted">—</span>;
  return <strong className={`text-${marginTone(bp)}`}>{percent(bp)}</strong>;
}
